// Principles: public outline + our one-line briefs; full Part I text and rubrics for active accounts.
import { useEffect, useState } from "react";
import { catalog, PRINCIPLE_BRIEFS, type Principle, type Rubric } from "../../shared/catalog";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { Paywall } from "../components/Paywall";
import { pageTitle } from "../brand";

export default function HandbookPage() {
  const { me } = useSession();
  const [full, setFull] = useState<{ principles: Principle[]; rubrics: Rubric[] } | null>(null);
  useEffect(() => {
    document.title = pageTitle("Principles & rubrics");
  }, []);
  useEffect(() => {
    if (me?.access.active) api.handbookContent().then(setFull).catch(() => setFull(null));
    else setFull(null);
  }, [me?.access.active]);

  const body = (id: string) => full?.principles.find((p) => p.id === id)?.body;
  const chapters = [{ n: 1, title: "Principles of effective lesson design" }, { n: 2, title: "Classroom management strategies" }];
  return (
    <div className="page reading">
      <h1>The principles behind every adaptation</h1>
      <p className="lead">When the AI changes a lesson, it cites one of these sections from Part I of <i>The ESL Teacher's Handbook</i> by Melina Wong.</p>
      <nav className="toc" aria-label="Sections">
        {catalog.principles.map((p) => <a key={p.id} href={`#p-${p.id.replace(".", "-")}`}>§{p.id} {p.title}</a>)}
        <a href="#rubrics">Assessment rubrics</a>
      </nav>
      {chapters.map((c) => (
        <section key={c.n}>
          <h2>Chapter {c.n}: {c.title}</h2>
          {catalog.principles.filter((p) => p.chapter === c.n).map((p) => (
            <article key={p.id} id={`p-${p.id.replace(".", "-")}`} className="principle">
              <h3><span className="code">§{p.id}</span> {p.title}</h3>
              {body(p.id)?.map((para, i) => <p key={i}>{para}</p>) ?? <p className="principle-brief">{PRINCIPLE_BRIEFS[p.id]}</p>}
            </article>
          ))}
        </section>
      ))}
      <section id="rubrics">
        <h2>Assessment rubrics</h2>
        {full ? full.rubrics.map((r) => (
          <div key={r.name} className="rubric-wrap">
            <h3>{r.name}</h3>
            <div className="table-scroll">
              <table className="rubric">
                <thead><tr><th scope="col">Criterion</th>{r.bands.map((b) => <th key={b} scope="col">{b}</th>)}</tr></thead>
                <tbody>{r.criteria.map((c) => <tr key={c.criterion}><th scope="row">{c.criterion}</th>{c.descriptors.map((d, i) => <td key={i}>{d}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
        )) : <p className="muted">{catalog.rubrics.map((r) => r.name).join(" and ")} (4 bands each).</p>}
      </section>
      {!full && <Paywall what="the full text of Part I and the assessment rubrics" />}
    </div>
  );
}
